// ============================================================================
// GRID LOADING SKELETON
// ============================================================================
// Displays an animated skeleton placeholder that mimics the SyutGrid table
// layout during data loading, preventing the shrink-expand flash.

import React from 'react';

// Inject styles once
if (typeof document !== 'undefined') {
  const styleId = 'grid-loading-skeleton-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes skeleton-shimmer {
        0% { background-position: -400px 0; }
        100% { background-position: 400px 0; }
      }
      .grid-skeleton-bar {
        background: linear-gradient(90deg, var(--border-color, #e2e8f0) 25%, rgba(226,232,240,0.4) 50%, var(--border-color, #e2e8f0) 75%);
        background-size: 800px 100%;
        animation: skeleton-shimmer 1.5s infinite ease-in-out;
        border-radius: 4px;
        height: 14px;
      }
    `;
    document.head.appendChild(style);
  }
}

interface GridLoadingSkeletonProps {
  rows?: number;
  columns?: number;
}

const GridLoadingSkeleton: React.FC<GridLoadingSkeletonProps> = ({ rows = 10, columns = 6 }) => {
  const colWidths = [
    ['12%', '22%', '14%', '16%', '18%', '18%'],
    ['15%', '25%', '15%', '15%', '15%', '15%'],
    ['10%', '20%', '15%', '20%', '15%', '20%'],
  ];
  const widthSet = colWidths[0];

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Skeleton header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        borderBottom: '2px solid var(--border-color, #e2e8f0)',
        background: 'var(--glass-bg, rgba(255,255,255,0.7))',
        minHeight: '42px',
      }}>
        {/* Checkbox placeholder */}
        <div style={{ width: 20, height: 14 }} className="grid-skeleton-bar" />
        {Array.from({ length: Math.min(columns, widthSet.length) }).map((_, i) => (
          <div
            key={`hdr-${i}`}
            className="grid-skeleton-bar"
            style={{ width: widthSet[i], height: 12 }}
          />
        ))}
      </div>

      {/* Skeleton rows */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <div
            key={`row-${rowIdx}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              borderBottom: '1px solid var(--border-color, #e2e8f0)',
            }}
          >
            {/* Checkbox placeholder */}
            <div style={{ width: 20, height: 14 }} className="grid-skeleton-bar" />
            {Array.from({ length: Math.min(columns, widthSet.length) }).map((_, colIdx) => (
              <div
                key={`cell-${rowIdx}-${colIdx}`}
                className="grid-skeleton-bar"
                style={{
                  width: widthSet[colIdx],
                  height: 14,
                  animationDelay: `${(rowIdx * 0.05 + colIdx * 0.03).toFixed(2)}s`,
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Skeleton pagination footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 16px',
        borderTop: '1px solid var(--border-color, #e2e8f0)',
        background: 'var(--glass-bg, rgba(255,255,255,0.7))',
        minHeight: '40px',
      }}>
        <div className="grid-skeleton-bar" style={{ width: '140px', height: 12 }} />
        <div style={{ display: 'flex', gap: '8px' }}>
          <div className="grid-skeleton-bar" style={{ width: '80px', height: 12 }} />
          <div className="grid-skeleton-bar" style={{ width: '60px', height: 12 }} />
        </div>
      </div>
    </div>
  );
};

export default GridLoadingSkeleton;
